/**
 * PremiumPlus (Classic 50/50) style-specific section renderers.
 * Extracted from Classic5050StylePremiumPlus.jsx for use in GridEngine.
 * Uses charity theme CSS variables for branding.
 */
import React from 'react'
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import ResponsiveImage from '../../components/ResponsiveImage'
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import HeroSiteLogo from '../../components/HeroSiteLogo'
import CountdownTimerStyles from '../../components/CountdownTimerStyles'
import DrawCountdownHeader from '../../components/DrawCountdownHeader'
import useHeroContent from '../../hooks/useHeroContent'
import RecentWinners from '../../components/RecentWinners'
import { SplitPotCharity } from '../../components/charity'
import PartnerBanner from '../../components/PartnerBanner'
import { useCharityTheme } from '../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../contexts/SiteConfigContext'
import { charityConfig } from '../../config/charity'
import usePartnerData from '../../hooks/usePartnerData'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import { formatCurrencyValue } from '../../utils/formatCurrency'

// Hero: Charity-themed gradient, logo card, split pot, countdown
export function PremiumPlusHeroSection({
  mainDraw, partner, t, editMode, handleTranslationUpdate, gameType = 'classic5050', sections,
  purchaseNavigate, timeLeft, shouldAnimate, countdownStyle, showPartnerSelector = true, language,
}) {
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}
  const showPartner = showPartnerSelector
  const c = t
  const heroContent = useHeroContent({
    mainDraw,
    partner,
    gameType,
    sections,
    fallbackTitle: gameType === 'chaseTheAce' ? 'Chase the Ace' : (gameType === 'modified5050' ? 'Modified 50/50' : '50/50'),
    fallbackSubtitle: gameType === 'chaseTheAce' ? "Draw a card each week for a chance to win the progressive jackpot!" : "Win half the pot while supporting our community.",
    fallbackCta: 'Get Your Tickets',
  })
  if (!mainDraw) return null

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const charityAmount = jackpotAmount
  const totalPot = jackpotAmount * 2
  const isCta = gameType === 'chaseTheAce'
  const ticketsSold = mainDraw?.total_tickets_sold
  const ticketPrice = mainDraw?.ticket_price ?? 5
  const drawCharityName = mainDraw?.charity_name
  const hasDrawCharityName = Boolean(drawCharityName && String(drawCharityName).trim().toLowerCase() !== 'local charity')
  const charityName = hasDrawCharityName ? drawCharityName : c?.('premiumPlus.localCharity', 'Local Charity') ?? 'Local Charity'
  const renderCharityName = () => hasDrawCharityName ? charityName : (
    <InlineTranslationEdit
      translationKey="premiumPlus.localCharity"
      value={c?.('premiumPlus.localCharity', 'Local Charity')}
      fallback="Local Charity"
      onUpdate={handleTranslationUpdate}
      editMode={editMode}
    />
  )

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
  const logoUrl = isDefaultLogo ? brandLogo : (branding?.logoUrl || brandLogo)
  const logoAlt = isDefaultLogo ? brandName : (branding?.charityName || brandName || 'Charity Partner')

  const heroGradientStyle = {
    backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
  }

  return (
    <div>
      <section className="relative flex items-center overflow-hidden" style={heroGradientStyle}>
        {/* Soft overlay */}
        <div className="absolute inset-0 bg-black/10 pointer-events-none" aria-hidden="true" />

        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none animate-pulse"
          style={{ backgroundColor: 'var(--charity-primary-contrast)' }} aria-hidden="true" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-15 pointer-events-none animate-pulse"
          style={{ backgroundColor: 'var(--charity-accent)', animationDelay: '1s' }} aria-hidden="true" />

        {/* Floating icons */}
        <div className="hidden sm:block absolute top-20 left-[10%] opacity-20 animate-bounce" style={{ animationDuration: '3s' }}>
          <DollarSign className="w-16 h-16" style={{ color: 'var(--charity-primary-contrast)' }} />
        </div>
        <div className="hidden sm:block absolute top-32 right-[15%] opacity-15 animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}>
          <Heart className="w-12 h-12" style={{ color: 'var(--charity-primary-contrast)' }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-3 pb-4 sm:pt-[66px] sm:pb-[84px]">
          <HeroSiteLogo tone="light" />
          {showPartner && (
            <div className="mb-4 lg:hidden">
              <HeroPartnerGlassSelect
                className="mx-auto max-w-sm"
                label={c?.('heroPartners.label', 'Partner') ?? 'Partner'}
                placeholder={c?.('heroPartners.choosePartner', 'Choose a partner') ?? 'Choose a partner'}
              />
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left */}
            <div className="text-center">
              {logoUrl && (
                <div className="mb-2 sm:mb-8 flex justify-center">
                  <div className="relative group">
                    <div className="absolute -inset-3 rounded-2xl blur-xl opacity-40 group-hover:opacity-60 transition-opacity"
                      style={{ background: 'linear-gradient(135deg, var(--charity-primary), var(--charity-accent))' }} />
                    <div className="relative bg-white rounded-xl sm:rounded-2xl px-3 py-2 sm:px-6 sm:py-4 shadow-2xl">
                      <ResponsiveImage src={logoUrl} alt={logoAlt} className="h-8 sm:h-20 lg:h-24 w-auto object-contain max-w-[160px] sm:max-w-[280px]" sizes="(max-width: 640px) 160px, 280px" />
                    </div>
                  </div>
                </div>
              )}

              {/* Live Badge */}
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-[10px] sm:text-sm font-bold mb-2 sm:mb-6"
                style={{ color: 'var(--charity-primary-contrast)' }}>
                <span className="relative flex h-2 w-2 sm:h-3 sm:w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--charity-accent)' }} />
                  <span className="relative inline-flex rounded-full h-2 w-2 sm:h-3 sm:w-3" style={{ backgroundColor: 'var(--charity-accent)' }} />
                </span>
                <InlineTranslationEdit translationKey={heroContent.eyebrowKey} value={heroContent.eyebrowLabel} fallback="Operated by" onUpdate={handleTranslationUpdate} editMode={editMode} />
                {heroContent.showOperatorName && <span> {heroContent.operatorName}</span>}{' '}
                <InlineTranslationEdit translationKey="premiumPlus.liveBadgeSeparator" value={c?.('premiumPlus.liveBadgeSeparator', '—')} fallback="—" onUpdate={handleTranslationUpdate} editMode={editMode} />{' '}
                <InlineTranslationEdit translationKey="premiumPlus.liveNow" value={c?.('premiumPlus.liveNow', 'LIVE NOW')} fallback="LIVE NOW" onUpdate={handleTranslationUpdate} editMode={editMode} />
              </div>

              <h1 className="text-2xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-1 sm:mb-4 leading-tight" style={{ color: 'var(--charity-primary-contrast)' }}>
                <span className="block">{heroContent.organizationName}</span>
                <span className="block text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, var(--charity-primary-contrast), var(--charity-accent), var(--charity-primary-contrast))' }}>
                  <InlineTranslationEdit translationKey={heroContent.bannerKey} value={heroContent.bannerText} fallback={heroContent.bannerFallback} onUpdate={handleTranslationUpdate} editMode={editMode} />
                </span>
              </h1>

              <p className="hidden sm:block text-xl md:text-2xl mb-8 max-w-xl mx-auto" style={{ color: 'color-mix(in oklab, var(--charity-primary-contrast) 80%, transparent)' }}>
                <InlineTranslationEdit translationKey={heroContent.subtitleKey} value={heroContent.subtitleText} fallback={heroContent.subtitleFallback} onUpdate={handleTranslationUpdate} editMode={editMode} multiline />
              </p>

              {/* Mobile Pot Card */}
              <div className="lg:hidden mb-2 sm:mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-3xl p-2 sm:p-6 border border-white/20">
                  {isCta ? (
                    <>
                      <p className="font-bold uppercase text-[9px] sm:text-sm tracking-wider mb-0.5 sm:mb-4 text-center" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c?.('chaseTheAce.progressiveJackpot', 'Progressive Jackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-2xl sm:text-5xl md:text-6xl font-black text-center mb-1.5 sm:mb-4" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(jackpotAmount, { language })}</p>
                      {weeklyPrize >= 1 && (
                        <div className="rounded-md sm:rounded-xl p-1.5 sm:p-3 text-center" style={{ backgroundColor: 'color-mix(in oklab, var(--charity-primary) 30%, transparent)' }}>
                          <p className="text-[9px] sm:text-xs mb-0 sm:mb-1" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c?.('chaseTheAce.weeklyPrize', 'Weekly Prize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-xs sm:text-xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(weeklyPrize, { language })}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-bold uppercase text-[9px] sm:text-sm tracking-wider mb-0.5 sm:mb-4 text-center" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="premiumPlus.totalPot" value={c?.('premiumPlus.totalPot', 'Total Pot')} fallback="Total Pot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-2xl sm:text-5xl md:text-6xl font-black text-center mb-1.5 sm:mb-4" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(totalPot, { language })}</p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-4">
                        <div className="rounded-md sm:rounded-xl p-1.5 sm:p-3 text-center" style={{ backgroundColor: 'color-mix(in oklab, var(--charity-primary) 30%, transparent)' }}>
                          <p className="text-[9px] sm:text-xs mb-0 sm:mb-1" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="premiumPlus.winnerGets" value={c?.('premiumPlus.winnerGets', 'Winner Gets')} fallback="Winner Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-xs sm:text-xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(jackpotAmount, { language })}</p>
                        </div>
                        <div className="rounded-md sm:rounded-xl p-1.5 sm:p-3 text-center" style={{ backgroundColor: 'color-mix(in oklab, var(--charity-secondary) 30%, transparent)' }}>
                          <p className="text-[9px] sm:text-xs mb-0 sm:mb-1" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="premiumPlus.charityGets" value={c?.('premiumPlus.charityGets', 'Charity Gets')} fallback="Charity Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-xs sm:text-xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(charityAmount, { language })}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={() => purchaseNavigate?.('/purchase')}
                className="group relative w-full sm:w-auto px-5 sm:px-10 py-2.5 sm:py-5 bg-white text-sm sm:text-xl font-black rounded-lg sm:rounded-2xl overflow-hidden transition-all hover:scale-105 hover:shadow-2xl"
                style={{ color: 'var(--charity-primary)' }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                  <Ticket className="w-4 h-4 sm:w-6 sm:h-6" />
                  <InlineTranslationEdit translationKey={heroContent.ctaKey} value={heroContent.ctaText} fallback={heroContent.ctaFallback} onUpdate={handleTranslationUpdate} editMode={editMode} />
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>

              {/* Quick stats */}
              <div className="mt-2 sm:mt-6 hidden sm:flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm" style={{ color: 'color-mix(in oklab, var(--charity-primary-contrast) 80%, transparent)' }}>
                {ticketsSold != null && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span><strong style={{ color: 'var(--charity-primary-contrast)' }}>{ticketsSold.toLocaleString()}</strong> <InlineTranslationEdit translationKey="premiumPlus.ticketsSold" value={c?.('premiumPlus.ticketsSold', 'tickets sold')} fallback="tickets sold" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4" />
                  <span><strong style={{ color: 'var(--charity-primary-contrast)' }}>${ticketPrice}</strong> <InlineTranslationEdit translationKey="premiumPlus.perTicket" value={c?.('premiumPlus.perTicket', 'per ticket')} fallback="per ticket" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                </div>
              </div>
            </div>

            {/* Right — Desktop Pot Card */}
            <div className="hidden lg:flex flex-col items-center gap-6">
              {showPartner && (
                <HeroPartnerGlassSelect
                  className="w-full max-w-md"
                  label={c?.('heroPartners.label', 'Partner') ?? 'Partner'}
                  placeholder={c?.('heroPartners.choosePartner', 'Choose a partner') ?? 'Choose a partner'}
                />
              )}

              <div className="relative w-full max-w-md">
                <div className="absolute -inset-4 rounded-3xl blur-xl opacity-50"
                  style={{ background: 'linear-gradient(135deg, var(--charity-primary), var(--charity-accent))' }} />
                <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/30">
                  {isCta ? (
                    <>
                      <p className="font-bold uppercase text-sm tracking-wider mb-2 text-center" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c?.('chaseTheAce.progressiveJackpot', 'Progressive Jackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-6xl xl:text-7xl font-black text-center mb-6" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(jackpotAmount, { language })}</p>
                      {weeklyPrize >= 1 && (
                        <div className="rounded-xl p-4 text-center border" style={{ backgroundColor: 'color-mix(in oklab, var(--charity-primary) 20%, transparent)', borderColor: 'color-mix(in oklab, var(--charity-primary) 30%, transparent)' }}>
                          <Trophy className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--charity-accent)' }} />
                          <p className="text-xs mb-1" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c?.('chaseTheAce.weeklyPrize', 'Weekly Prize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-2xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(weeklyPrize, { language })}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-bold uppercase text-sm tracking-wider mb-2 text-center" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="premiumPlus.totalPot" value={c?.('premiumPlus.totalPot', 'Total Pot')} fallback="Total Pot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-6xl xl:text-7xl font-black text-center mb-6" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(totalPot, { language })}</p>

                      <div className="relative h-8 bg-gray-900/30 rounded-full overflow-hidden mb-4">
                        <div className="absolute inset-y-0 left-0 w-1/2 flex items-center justify-center"
                          style={{ backgroundImage: 'linear-gradient(to right, var(--charity-primary), var(--charity-accent))' }}>
                          <span className="text-xs font-bold" style={{ color: 'var(--charity-primary-contrast)' }}><InlineTranslationEdit translationKey="premiumPlus.fiftyYou" value={c?.('premiumPlus.fiftyYou', '50% YOU')} fallback="50% YOU" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                        </div>
                        <div className="absolute inset-y-0 right-0 w-1/2 flex items-center justify-center"
                          style={{ backgroundImage: 'linear-gradient(to right, var(--charity-secondary), var(--charity-accent))' }}>
                          <span className="text-xs font-bold" style={{ color: 'var(--charity-primary-contrast)' }}><InlineTranslationEdit translationKey="premiumPlus.fiftyCharity" value={c?.('premiumPlus.fiftyCharity', '50% CHARITY')} fallback="50% CHARITY" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-xl p-4 text-center border" style={{ backgroundColor: 'color-mix(in oklab, var(--charity-primary) 20%, transparent)', borderColor: 'color-mix(in oklab, var(--charity-primary) 30%, transparent)' }}>
                          <Trophy className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--charity-accent)' }} />
                          <p className="text-xs mb-1" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="premiumPlus.winnerGets" value={c?.('premiumPlus.winnerGets', 'Winner Gets')} fallback="Winner Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-2xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(jackpotAmount, { language })}</p>
                        </div>
                        <div className="rounded-xl p-4 text-center border" style={{ backgroundColor: 'color-mix(in oklab, var(--charity-secondary) 20%, transparent)', borderColor: 'color-mix(in oklab, var(--charity-secondary) 30%, transparent)' }}>
                          <Heart className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--charity-accent)' }} />
                          <p className="text-xs mb-1" style={{ color: 'var(--charity-accent)' }}><InlineTranslationEdit translationKey="premiumPlus.charityGets" value={c?.('premiumPlus.charityGets', 'Charity Gets')} fallback="Charity Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-2xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>{formatCurrencyValue(charityAmount, { language })}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Countdown */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
                <DrawCountdownHeader
                  draw={mainDraw}
                  className="mb-3"
                  labelStyle={{ color: 'var(--charity-accent)' }}
                >
                  <InlineTranslationEdit translationKey="premiumPlus.drawIn" value={c?.('premiumPlus.drawIn', 'Draw In')} fallback="Draw In" onUpdate={handleTranslationUpdate} editMode={editMode} />
                </DrawCountdownHeader>
                <div className="flex justify-center">
                  <CountdownTimerStyles
                    timeLeft={timeLeft}
                    shouldAnimate={shouldAnimate}
                    style={countdownStyle}
                    variant="dark"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Summary Bar */}
      <div className="relative z-20 -mt-16 pb-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="text-center md:text-left">
                {isCta ? (
                  <>
                    <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--charity-primary)' }}><InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c?.('chaseTheAce.progressiveJackpot', 'Progressive Jackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(jackpotAmount, { language })}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--charity-primary)' }}><InlineTranslationEdit translationKey="premiumPlus.yourPrize" value={c?.('premiumPlus.yourPrize', 'Your Prize')} fallback="Your Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(jackpotAmount, { language })}</p>
                  </>
                )}
              </div>
              <div className="text-center border-l border-r border-gray-200 dark:border-gray-700 px-6">
                {isCta ? (
                  <>
                    <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--charity-secondary)' }}><InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c?.('chaseTheAce.weeklyPrize', 'Weekly Prize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(weeklyPrize, { language })}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--charity-secondary)' }}>{renderCharityName()} <InlineTranslationEdit translationKey="premiumPlus.gets" value={c?.('premiumPlus.gets', 'Gets')} fallback="Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(charityAmount, { language })}</p>
                  </>
                )}
              </div>
              <div className="text-center md:text-right">
                <button
                  onClick={() => purchaseNavigate?.('/purchase')}
                  className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl hover:scale-105 transition-all shadow-lg"
                  style={{ backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))' }}
                >
                  <Ticket className="w-5 h-5" />
                  <InlineTranslationEdit translationKey="premiumPlus.getTickets" value={c?.('premiumPlus.getTickets', 'Get Tickets')} fallback="Get Tickets" onUpdate={handleTranslationUpdate} editMode={editMode} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Top Partners: Multi-partner grid
export function PremiumPlusTopPartnersSection({
  partner, t, editMode, handleTranslationUpdate,
}) {
  const c = t
  const { siteDetails } = useSiteConfig() || {}
  const isPartnersEnabled = siteDetails?.isPartnerDropdown !== false
  const { partners: topPartners = [] } = usePartnerData(3, isPartnersEnabled, null, true, null, 'RecentSupporters')
  if (!topPartners?.length) return null

  return (
    <section className="py-12 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
          <Gift className="w-7 h-7" style={{ color: 'var(--charity-primary)' }} />
          <InlineTranslationEdit translationKey="premiumPlus.topPartners" value={c?.('premiumPlus.topPartners', 'Top Partners')} fallback="Top Partners" onUpdate={handleTranslationUpdate} editMode={editMode} />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {topPartners.map((p) => (
            <div
              key={p.id}
              className="rounded-2xl p-6 shadow-lg"
              style={{
                backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
                color: 'var(--charity-primary-contrast)',
              }}
            >
              {p.logo_url && (
                <img src={p.logo_url} alt={p.name} className="h-12 w-auto object-contain mb-3" />
              )}
              <h3 className="text-lg font-bold">{p.name}</h3>
              {p.description && <p className="text-sm opacity-80 mt-1">{p.description}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
